import type { CalendarEvent } from '../stores/calendar'
import { blankTask, type DocketTask } from './docketModel'
import { expandEvents } from './calendarRepeat'
import { addDaysToKey } from './writingDays'

// Prep tasks: an event can ask for a Docket task some days before it ("Read
// the submissions", the day before the workshop). Each event has at most one,
// with an id fixed by the event, so every device works out the same task. It
// is one row, like any repeating task: finishing it moves it on to the next
// occurrence. It remembers which occurrence it is for, so when that one is
// moved the task moves with it, and when it is skipped the task goes on to the
// next. The event is where its title and timing are set; the task can be
// moved to any list.

export function prepTaskId(eventId: string): string {
  return `prep-${eventId}`
}

const dayOf = (stored: string) => stored.slice(0, 10)
/** How far ahead to look for an event's next occurrence. */
const LOOKAHEAD_DAYS = 400
/** How far back a moved occurrence may have gone. */
const LOOKBACK_DAYS = 60

interface Live {
  /** The day the series planned it for (for a single event, its day). */
  key: string
  /** The day it now falls on. */
  day: string
}

/** The event's occurrences that still happen, in order of the day planned, from around `from`. */
function liveOccurrences(event: CalendarEvent, events: CalendarEvent[], from: string): Live[] {
  if (!event.repeat) return [{ key: dayOf(event.start), day: dayOf(event.start) }]
  const own = events.filter((e) => e.id === event.id || e.seriesId === event.id)
  return expandEvents(own, addDaysToKey(from, -LOOKBACK_DAYS), addDaysToKey(from, LOOKAHEAD_DAYS))
    .map(({ event: shown, occurrence }) => ({ key: occurrence!, day: dayOf(shown.start) }))
    .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
}

/** The task's dates and link for an occurrence. */
function forOccurrence(event: CalendarEvent, live: Live): Pick<DocketTask, 'doOn' | 'repeat'> {
  const leadDays = event.prep!.leadDays
  return {
    doOn: addDaysToKey(live.day, -leadDays),
    repeat: { kind: 'event', eventId: event.id, leadDays, occurrence: live.key },
  }
}

/**
 * Where a prep task goes once finished on `finishedOn`: the next occurrence
 * after the one it was for that hasn't already happened. null when the event
 * has no more.
 */
export function nextPrepDates(task: DocketTask, events: CalendarEvent[], finishedOn: string): Pick<DocketTask, 'doOn' | 'repeat'> | null {
  if (task.repeat?.kind !== 'event') return null
  const current = task.repeat.occurrence
  const event = events.find((e) => e.id === (task.repeat as { eventId: string }).eventId)
  if (!event?.prep) return null
  const next = liveOccurrences(event, events, current < finishedOn ? current : finishedOn)
    .find((live) => live.key > current && live.day >= finishedOn)
  return next ? forOccurrence(event, next) : null
}

export interface PrepChanges {
  save: DocketTask[]
  remove: string[]
}

/**
 * Brings prep tasks in line with the events: makes the task for an event
 * that asks for one, keeps its title, link, and date in step with the
 * occurrence it is for (moving on when that one is skipped), and removes it
 * when the event, or its request for one, is gone. Finished tasks are left
 * alone. Changing nothing when all is in step, it is safe to run on any
 * change, on any device.
 */
export function planPrepTasks(
  events: CalendarEvent[],
  tasks: DocketTask[],
  today: string,
  now: number,
  /** The list new tasks are made in; null is the Inbox. */
  listId: string | null = null,
): PrepChanges {
  const changes: PrepChanges = { save: [], remove: [] }
  const byId = new Map(tasks.map((task) => [task.id, task]))
  const wanted = new Set<string>()
  for (const event of events) {
    if (event.seriesId || !event.prep) continue
    const id = prepTaskId(event.id)
    wanted.add(id)
    const task = byId.get(id)
    if (task?.done) continue
    const current = task?.repeat?.kind === 'event' ? task.repeat.occurrence : null
    const occurrences = liveOccurrences(event, events, current && current < today ? current : today)
    // A new task starts at the next occurrence to come; an existing one keeps
    // to its occurrence, or the next one after it if that was skipped.
    // A single event's task simply follows the event.
    const target = !event.repeat
      ? (task || occurrences[0].day >= today ? occurrences[0] : undefined)
      : current
        ? occurrences.find((live) => live.key >= current)
        : occurrences.find((live) => live.day >= today)
    if (!target) {
      if (task) changes.remove.push(id)
      continue
    }
    const next: DocketTask = {
      ...(task ?? blankTask(id, now, { listId })),
      title: event.prep.title,
      linkedId: event.linkedId,
      ...forOccurrence(event, target),
    }
    if (!task || JSON.stringify(next) !== JSON.stringify(task)) changes.save.push(next)
  }
  for (const task of tasks) {
    if (task.repeat?.kind === 'event' && !task.done && !wanted.has(task.id)) changes.remove.push(task.id)
  }
  return changes
}
